import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { StatusBadge } from "@/components/Badges";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Loader2, Search, FileText, UserCheck, Clock, Upload, Download, Trash2, AlertCircle, AlertTriangle, CheckCircle2 } from "lucide-react";
import { ConfirmDialog } from "@/components/admin/ConfirmDialog";
import { PageLoader, LoadError } from "@/components/admin/LoadState";
import { saveBlob } from "@/components/admin/PointsUploadPanel";
import { ImportLog } from "@/components/admin/ImportLog";
import { usePageTitle } from "@/hooks/usePageTitle";
import { fmtNum, norm, plural } from "@/lib/format";

type Doc = {
  cedula: string;
  name: string;
  initial_points: number;
  claimed: boolean;
  registration_status: string | null;
  account_status: string | null;
  registered_name: string | null;
  registered_email: string | null;
};

type Preview = {
  total: number; valid: number; new: number; updates: number; with_points: number; applied: boolean;
  errors: { row: number; cedula: string; error: string }[];
  warnings: { row: number; cedula: string; warning: string }[];
};

export default function AdminDocuments() {
  usePageTitle("Carga de documentos · Gestión de clientes");
  const [docs, setDocs] = useState<Doc[]>([]);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [params, setParams] = useSearchParams();
  const q = params.get("q") || "";
  const tab = (params.get("tab") || "all") as "all" | "claimed" | "pending";
  const setParam = (k: string, v: string) => {
    const next = new URLSearchParams(params);
    if (v && !(k === "tab" && v === "all")) next.set(k, v); else next.delete(k);
    setParams(next, { replace: true });
  };
  const [uploadOpen, setUploadOpen] = useState(false);
  const [logRefresh, setLogRefresh] = useState(0);
  const [deleteTarget, setDeleteTarget] = useState<Doc | null>(null);

  const load = () => {
    setFailed(false);
    return api.get("/admin/preloaded-clients").then((r) => setDocs(r.data)).catch(() => setFailed(true)).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const claimedCount = docs.filter((d) => d.claimed).length;
  const pendingCount = docs.length - claimedCount;
  const filtered = docs
    .filter((d) => tab === "all" || (tab === "claimed" ? d.claimed : !d.claimed))
    .filter((d) => !q || [d.cedula, d.name, d.registered_name, d.registered_email].some((v) => norm(v).includes(norm(q.trim()))));

  const confirmDelete = async () => {
    try {
      await api.delete(`/admin/preloaded-clients/${encodeURIComponent(deleteTarget!.cedula)}`);
      toast.success(`La cédula ${deleteTarget!.cedula} ya no está autorizada`);
      load();
    } catch (err: any) { toast.error(apiError(err)); throw err; }
  };

  if (loading) return <PageLoader />;
  if (failed) return <LoadError what="las cédulas autorizadas" onRetry={load} />;

  return (
    <div className="space-y-6 animate-fade-in" data-testid="admin-documents-page">
      <div className="flex justify-end">
        <Button data-testid="bulk-upload-button" onClick={() => setUploadOpen(true)} className="gold-gradient-bg text-white font-semibold h-11 rounded-full shrink-0">
          <Upload className="w-4 h-4 mr-1.5" aria-hidden="true" /> Cargar documentos
        </Button>
      </div>

      <div className="grid sm:grid-cols-3 gap-4">
        <SummaryCard icon={FileText} label="Cédulas autorizadas" value={docs.length} color="text-gold-ink" />
        <SummaryCard icon={UserCheck} label="Ya se registraron" value={claimedCount} color="text-emerald-700" />
        <SummaryCard icon={Clock} label="Aún no se registran" value={pendingCount} color="text-amber-700" />
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap gap-2">
          {[
            { key: "all", label: "Todas" },
            { key: "claimed", label: `Registradas${claimedCount ? ` (${fmtNum(claimedCount)})` : ""}` },
            { key: "pending", label: `Sin registrar${pendingCount ? ` (${fmtNum(pendingCount)})` : ""}` },
          ].map((t) => (
            <button
              key={t.key}
              data-testid={`doc-tab-${t.key}`}
              onClick={() => setParam("tab", t.key)}
              aria-pressed={tab === t.key}
              className={`px-4 py-1.5 rounded-full text-sm border transition-colors ${
                tab === t.key ? "gold-gradient-bg text-white font-semibold border-transparent" : "border-slate-200 text-slate-600 hover:text-slate-900"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>
        <div className="relative w-full sm:w-72">
          <Search className="w-4 h-4 text-slate-500 absolute left-3 top-1/2 -translate-y-1/2" aria-hidden="true" />
          <Input data-testid="doc-search-input" aria-label="Buscar cédulas" value={q} onChange={(e: any) => setParam("q", e.target.value)}
            placeholder="Buscar por cédula o nombre" className="pl-9 bg-obsidian-card border-slate-200 text-slate-900 h-11" />
        </div>
      </div>

      <div className="card-obsidian overflow-hidden">
        {filtered.length === 0 ? (
          <p className="px-5 py-10 text-center text-slate-600">{q ? <>No hay cédulas que coincidan con “{q}”.</> : "No hay cédulas en esta lista."}</p>
        ) : (
          <>
            <div className="hidden md:block overflow-x-auto" tabIndex={0} role="region" aria-label="Tabla de cédulas autorizadas">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-slate-200 text-slate-600 text-xs uppercase tracking-wider">
                    <th scope="col" className="text-left font-medium px-5 py-3">Cédula</th>
                    <th scope="col" className="text-left font-medium px-5 py-3">Nombre</th>
                    <th scope="col" className="text-right font-medium px-5 py-3">Puntos iniciales</th>
                    <th scope="col" className="text-left font-medium px-5 py-3">Registro</th>
                    <th scope="col" className="text-left font-medium px-5 py-3">Aprobación</th>
                    <th scope="col" className="text-right font-medium px-5 py-3"><span className="sr-only">Acciones</span></th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((d) => (
                    <tr key={d.cedula} data-testid="admin-doc-row" className="border-b border-slate-200 hover:bg-slate-50">
                      <td className="px-5 py-3 font-mono text-slate-800">{d.cedula}</td>
                      <td className="px-5 py-3 text-slate-700">
                        {d.registered_name || d.name || <span className="text-slate-500">—</span>}
                        {d.registered_email && <p className="text-xs text-slate-600">{d.registered_email}</p>}
                      </td>
                      <td className="px-5 py-3 text-right font-mono text-slate-700">{d.initial_points ? fmtNum(d.initial_points) : "—"}</td>
                      <td className="px-5 py-3"><Registered d={d} /></td>
                      <td className="px-5 py-3">{d.claimed && d.registration_status ? <StatusBadge status={d.registration_status} /> : <span className="text-slate-500 text-xs">—</span>}</td>
                      <td className="px-5 py-3 text-right">
                        <Button data-testid={`delete-doc-${d.cedula}`} size="sm" variant="outline" onClick={() => setDeleteTarget(d)}
                          className="border-red-500/30 text-red-700 hover:bg-red-500/10 h-9" title="Quitar autorización" aria-label={`Quitar la autorización de la cédula ${d.cedula}`}>
                          <Trash2 className="w-3.5 h-3.5" aria-hidden="true" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <ul className="md:hidden divide-y divide-slate-200">
              {filtered.map((d) => (
                <li key={d.cedula} className="p-4 flex items-start justify-between gap-3">
                  <div className="min-w-0 space-y-1">
                    <p className="font-mono text-slate-900">{d.cedula}</p>
                    <p className="text-sm text-slate-700">{d.registered_name || d.name || "Sin nombre"}</p>
                    <Registered d={d} />
                  </div>
                  <Button size="sm" variant="outline" onClick={() => setDeleteTarget(d)} className="border-red-500/30 text-red-700 h-9" aria-label={`Quitar la autorización de la cédula ${d.cedula}`}>
                    <Trash2 className="w-3.5 h-3.5" aria-hidden="true" />
                  </Button>
                </li>
              ))}
            </ul>
          </>
        )}
      </div>

      <ImportLog kind="cedulas" refreshKey={logRefresh} />

      <UploadCedulasDialog open={uploadOpen} onOpenChange={setUploadOpen} onApplied={() => { load(); setLogRefresh((n) => n + 1); }} />

      <ConfirmDialog
        open={!!deleteTarget}
        onOpenChange={(o) => !o && setDeleteTarget(null)}
        danger
        title="¿Quitar la autorización?"
        description={<>
          <p>La cédula <span className="font-mono">{deleteTarget?.cedula}</span> ya no podrá usarse para registrarse.</p>
          {deleteTarget?.claimed && <p className="text-amber-800">Esta cédula ya tiene una cuenta. Quitarla de la lista <strong>no elimina la cuenta del cliente</strong>, pero dejará de aparecer aquí.</p>}
          <p>Esta acción no se puede deshacer.</p>
        </>}
        confirmLabel="Quitar autorización"
        onConfirm={confirmDelete}
        testid="delete-doc-dialog"
      />
    </div>
  );
}

const Registered = ({ d }: { d: Doc }) => d.claimed ? (
  <span className="inline-flex items-center gap-1.5 text-xs font-medium text-emerald-800"><UserCheck className="w-3.5 h-3.5" aria-hidden="true" /> Ya se registró</span>
) : (
  <span className="inline-flex items-center gap-1.5 text-xs font-medium text-amber-800"><Clock className="w-3.5 h-3.5" aria-hidden="true" /> Aún no se registra</span>
);

const SummaryCard = ({ icon: Icon, label, value, color }: any) => (
  <div className="card-obsidian p-5">
    <div className="flex items-center gap-2 text-slate-600 text-sm mb-3">
      <Icon className={`w-4 h-4 ${color}`} aria-hidden="true" /> {label}
    </div>
    <p className={`text-3xl font-serif font-medium ${color}`}>{fmtNum(value)}</p>
  </div>
);

// Autorizar cédulas: se revisa el archivo (o el texto pegado) y nada se carga hasta confirmar.
function UploadCedulasDialog({ open, onOpenChange, onApplied }: { open: boolean; onOpenChange: (o: boolean) => void; onApplied: () => void }) {
  const [file, setFile] = useState<File | null>(null);
  const [text, setText] = useState("");
  const [preview, setPreview] = useState<Preview | null>(null);
  const [busy, setBusy] = useState(false);

  const reset = () => { setFile(null); setText(""); setPreview(null); };
  const close = (o: boolean) => { if (!o) reset(); onOpenChange(o); };
  // El texto pegado se envía como CSV al mismo endpoint, así se valida igual que un archivo.
  const source = () => file || (text.trim() ? new File([text], "pegado.csv", { type: "text/csv" }) : null);

  const send = async (confirm: boolean) => {
    const f = source();
    if (!f) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post(`/admin/preloaded-clients/upload?confirm=${confirm}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setPreview(data);
      if (confirm) {
        toast.success(`Listo: ${plural(data.new, "cédula nueva", "cédulas nuevas")} y ${plural(data.updates, "actualizada", "actualizadas")}.`);
        onApplied();
      }
    } catch (err: any) { toast.error(apiError(err)); }
    finally { setBusy(false); }
  };

  const downloadTemplate = async () => {
    try {
      const { data } = await api.get("/admin/preloaded-clients/template", { responseType: "blob" });
      saveBlob(data, "plantilla_cedulas_autorizadas.xlsx");
    } catch { toast.error("No se pudo descargar la plantilla"); }
  };

  const applied = !!preview?.applied;

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent className="bg-obsidian-card border-slate-200 text-slate-900 sm:max-w-2xl" data-testid="upload-cedulas-dialog">
        <DialogHeader><DialogTitle className="font-serif text-xl">Cargar documentos autorizados</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <p className="text-sm text-slate-700">
            Solo la <strong>cédula</strong> es obligatoria. Opcionalmente: nombre, puntos iniciales (se suman al saldo cuando la persona se registra),
            teléfono y fecha de nacimiento (AAAA-MM-DD o DD/MM/AAAA, para el regalo de cumpleaños). La hoja “Instrucciones” de la plantilla explica cada columna.
            Primero revisamos el archivo; nada se autoriza hasta que confirmes, y cada carga queda en el historial.
          </p>
          <div>
            <div className="flex items-center justify-between mb-1.5">
              <Label htmlFor="cedulas-file" className="text-slate-700 text-sm">Archivo Excel (.xlsx) o CSV</Label>
              <button type="button" data-testid="download-template-button" onClick={downloadTemplate} className="text-sm text-navy hover:underline flex items-center gap-1">
                <Download className="w-3.5 h-3.5" aria-hidden="true" /> Descargar plantilla
              </button>
            </div>
            <Input id="cedulas-file" data-testid="bulk-file-input" type="file" accept=".csv,.txt,.xlsx" disabled={busy || applied}
              onChange={(e: any) => { const f = e.target.files?.[0]; e.target.value = ""; if (f) { setFile(f); setText(""); setPreview(null); } }}
              className="bg-obsidian border-slate-200 text-slate-900" />
            {file && <p className="text-xs text-slate-600 mt-1.5 font-mono">{file.name}</p>}
            <p className="text-xs text-slate-600 mt-1.5">Usa la plantilla: su columna de cédula está en formato texto para que Excel no borre el cero inicial.</p>
          </div>
          {!file && (
            <div>
              <Label htmlFor="cedulas-text" className="text-slate-700 text-sm">O pega las cédulas (una por línea)</Label>
              <Textarea id="cedulas-text" data-testid="bulk-textarea" rows={5} value={text} disabled={busy || applied}
                onChange={(e: any) => { setText(e.target.value); setPreview(null); }} placeholder={"0102030405\n1712345678; Ana Gómez; 500"}
                className="mt-1.5 bg-obsidian border-slate-200 text-slate-900 font-mono text-sm" />
            </div>
          )}

          {preview && (
            <div className="space-y-3" data-testid="cedulas-preview">
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-center text-sm">
                <Box label="Filas" value={preview.total} />
                <Box label={applied ? "Nuevas autorizadas" : "Nuevas"} value={preview.new} ok />
                <Box label={applied ? "Actualizadas" : "Ya existían (se actualizan)"} value={preview.updates} />
                <Box label="Con error" value={preview.errors.length} bad={preview.errors.length > 0} />
              </div>
              {applied && <p className="text-sm text-emerald-800 flex items-center gap-1.5" role="status"><CheckCircle2 className="w-4 h-4" aria-hidden="true" /> Las cédulas válidas ya pueden registrarse.</p>}
              {preview.with_points > 0 && !applied && <p className="text-sm text-slate-700">{plural(preview.with_points, "fila trae", "filas traen")} puntos iniciales: se sumarán cuando esa persona se registre.</p>}
              {preview.errors.length > 0 && (
                <Issues title={applied ? "Filas que no se cargaron" : "Estas filas no se cargarán"} tone="bad"
                  rows={preview.errors.map((e) => ({ row: e.row, cedula: e.cedula, text: e.error }))} />
              )}
              {preview.warnings.length > 0 && (
                <Issues title="Revisa estas cédulas (se cargarán igual)" tone="warn"
                  rows={preview.warnings.map((w) => ({ row: w.row, cedula: w.cedula, text: w.warning }))} />
              )}
            </div>
          )}
        </div>
        <DialogFooter>
          {applied ? (
            <Button onClick={() => close(false)} className="gold-gradient-bg text-white font-semibold rounded-full">Listo</Button>
          ) : (
            <>
              <Button variant="outline" onClick={() => close(false)} className="border-slate-200 text-slate-700 rounded-full">Cancelar</Button>
              {!preview ? (
                <Button data-testid="bulk-review-button" onClick={() => send(false)} disabled={busy || (!file && !text.trim())} className="gold-gradient-bg text-white font-semibold rounded-full">
                  {busy ? <Loader2 className="w-4 h-4 animate-spin" aria-label="Revisando" /> : "Revisar"}
                </Button>
              ) : (
                <Button data-testid="bulk-submit-button" onClick={() => send(true)} disabled={busy || preview.valid === 0} className="gold-gradient-bg text-white font-semibold rounded-full">
                  {busy ? <Loader2 className="w-4 h-4 animate-spin" aria-label="Autorizando" /> : <><Upload className="w-4 h-4 mr-1" aria-hidden="true" /> Autorizar {plural(preview.valid, "cédula", "cédulas")}</>}
                </Button>
              )}
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const Box = ({ label, value, ok, bad }: { label: string; value: number; ok?: boolean; bad?: boolean }) => (
  <div className={`p-3 rounded-lg border ${ok ? "bg-emerald-500/10 border-emerald-500/30" : bad ? "bg-red-500/10 border-red-500/30" : "bg-obsidian border-slate-200"}`}>
    <p className="text-xs text-slate-700">{label}</p>
    <p className="text-xl font-serif">{fmtNum(value)}</p>
  </div>
);

const Issues = ({ title, rows, tone }: { title: string; rows: { row: number; cedula: string; text: string }[]; tone: "bad" | "warn" }) => (
  <div>
    <p className={`text-sm flex items-center gap-1.5 mb-1.5 ${tone === "bad" ? "text-red-700" : "text-amber-800"}`}>
      {tone === "bad" ? <AlertCircle className="w-4 h-4" aria-hidden="true" /> : <AlertTriangle className="w-4 h-4" aria-hidden="true" />} {title}
    </p>
    <div className="max-h-40 overflow-y-auto rounded-lg border border-slate-200">
      <table className="w-full text-xs">
        <tbody>
          {rows.map((r) => (
            <tr key={`${r.row}-${r.cedula}`} className="border-b border-slate-200 last:border-0">
              <td className="px-3 py-2 font-mono text-slate-600">Fila {r.row}</td>
              <td className="px-3 py-2 font-mono text-slate-800">{r.cedula || "—"}</td>
              <td className={`px-3 py-2 ${tone === "bad" ? "text-red-700" : "text-amber-800"}`}>{r.text}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </div>
);
